const observedShadowRoots = new WeakSet()
const ELIGIBILITY_TIMEOUT_MS = 3000

if (!customElements.get('og-plp-custom-widget')) {
  class OgPlpCustomWidget extends HTMLElement {
    connectedCallback () {
      this._disconnected = false
      this.setupMutationObservers()
      this.initOfferState()
    }

    disconnectedCallback () {
      this._disconnected = true
      this.clearEligibilityTimeout()
      this.observer?.disconnect()
      this.widgetObserver?.disconnect()
      this.observer = null
      this.widgetObserver = null
      this._observersReady = false
    }

    resolveParentWrapper () {
      const closestOffer = this.closest('og-offer')
      if (closestOffer) return closestOffer

      const root = this.getRootNode()
      if (root instanceof ShadowRoot && root.host?.localName === 'og-offer') {
        return root.host
      }

      return null
    }

    resolveElements () {
      this.parentWrapper = this.resolveParentWrapper()

      if (!this.parentWrapper) {
        this.ogOffer = null
        this.offerWrapper = null
        this.productId = null
        return false
      }

      this.ogOffer = this.parentWrapper
      this.offerWrapper = this.ogOffer.closest('.product-item__offer-label')
      this.productId = this.ogOffer.getAttribute('product')
      return Boolean(this.offerWrapper)
    }

    isSubscribed () {
      return this.parentWrapper?.hasAttribute('subscribed') ?? false
    }

    observeWidgetUpdates () {
      if (!this.parentWrapper || this.widgetObserver) return

      this.widgetObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'subscribed') {
            this.updateVisibility()
          }
        }
      })

      this.widgetObserver.observe(this.parentWrapper, {
        attributes: true,
        attributeFilter: ['subscribed'],
      })
    }

    setupMutationObservers () {
      this.observer = new MutationObserver(() => {
        this.ensureShadowObservers()
        if (this.parentWrapper) this.updateVisibility()
      })

      this.observer.observe(this, { childList: true, subtree: true, characterData: true })
    }

    initOfferState () {
      let resolveAttempts = 0
      let stateAttempts = 0
      const maxAttempts = 40

      const tryInit = () => {
        if (this._disconnected) return

        if (!this.resolveElements()) {
          if (++resolveAttempts < maxAttempts) {
            requestAnimationFrame(tryInit)
          }
          return
        }

        if (!this._observersReady) {
          this._observersReady = true
          this.observeWidgetUpdates()
          this.ensureShadowObservers()
          this.scheduleEligibilityTimeout()
        }

        const state = this.getEligibilityState()

        if (state !== 'loading') {
          this.updateVisibility()
          return
        }

        if (++stateAttempts < maxAttempts) {
          requestAnimationFrame(tryInit)
        } else {
          this.updateVisibility()
        }
      }

      requestAnimationFrame(tryInit)
    }

    ensureShadowObservers () {
      if (!this.observer) return

      this.querySelectorAll('og-when').forEach((ogWhen) => {
        const root = ogWhen.shadowRoot
        if (!root || observedShadowRoots.has(root)) return

        observedShadowRoots.add(root)
        this.observer.observe(root, { childList: true, subtree: true, characterData: true })
      })
    }

    hasVisibleOfferLabel (root) {
      if (!root) return false

      for (const node of root.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          return true
        }
      }

      for (const el of root.querySelectorAll('*')) {
        if (el.matches('[data-product-eligible]')) continue
        if (el.textContent.trim()) return true
      }

      return false
    }

    hasNotEligibleMarker (root) {
      if (!root) return false

      return [...root.querySelectorAll('[data-product-eligible]')].some(
        (el) => el.textContent.trim() === 'NOT_ELIGIBLE'
      )
    }

    getOgWhenShadowRoot (testValue) {
      for (const ogWhen of this.querySelectorAll('og-when')) {
        if (ogWhen.getAttribute('test') === testValue) {
          return ogWhen.shadowRoot
        }
      }

      return null
    }

    getEligibilityState () {
      const regularEligibleRoot = this.getOgWhenShadowRoot('regularEligible')
      const notRegularEligibleRoot = this.getOgWhenShadowRoot('!regularEligible')

      if (regularEligibleRoot && this.hasVisibleOfferLabel(regularEligibleRoot)) {
        return 'eligible'
      }

      if (this.isSubscribed()) {
        return 'eligible'
      }

      if (
        notRegularEligibleRoot &&
        this.hasNotEligibleMarker(notRegularEligibleRoot) &&
        !this.hasVisibleOfferLabel(regularEligibleRoot)
      ) {
        return 'notEligible'
      }

      return 'loading'
    }

    scheduleEligibilityTimeout () {
      if (this._eligibilityTimeout) return

      this._eligibilityTimeout = setTimeout(() => {
        this._eligibilityTimeout = null
        if (this._disconnected) return

        this.resolveElements()
        if (this.getEligibilityState() !== 'eligible') {
          this.hideOffer()
        }
      }, ELIGIBILITY_TIMEOUT_MS)
    }

    clearEligibilityTimeout () {
      if (!this._eligibilityTimeout) return
      clearTimeout(this._eligibilityTimeout)
      this._eligibilityTimeout = null
    }

    hideOffer () {
      if (!this.ogOffer || !this.offerWrapper) return

      this.offerWrapper.classList.remove('product-item__offer-label--loading')
      this.offerWrapper.classList.add('product-item__offer-label--hidden')
      this.ogOffer.style.display = 'none'
    }

    updateVisibility () {
      if (!this.ogOffer || !this.offerWrapper) return

      const state = this.getEligibilityState()

      if (state === 'eligible') {
        this.clearEligibilityTimeout()
        this.offerWrapper.classList.remove('product-item__offer-label--loading', 'product-item__offer-label--hidden')
        this.ogOffer.style.display = ''
        return
      }

      if (state === 'notEligible') {
        this.clearEligibilityTimeout()
        this.hideOffer()
        return
      }

      this.offerWrapper.classList.add('product-item__offer-label--loading')
      this.offerWrapper.classList.remove('product-item__offer-label--hidden')
      this.ogOffer.style.display = ''
    }
  }

  customElements.define('og-plp-custom-widget', OgPlpCustomWidget)
}
